import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Table from "../../../../components/ACS-Dashboard/ACS-Blocks/Table";
import Loader from "../../../../components/ACS-Dashboard/ACS-Blocks/Loader";
import { MoreHorizontal } from "lucide-react";
import ActionMenu from "../../../../components/ACS-Dashboard/ACS-Blocks/ActionMenu";
import Cookies from "js-cookie";
import { useDispatch } from "react-redux";
import { addTab } from "../../../../redux/slices/documentUploaderSlice"; 

const TABLE_HEADERS = [
  "Selection",
  "Action",
  "Document ID",
  "Ascend Score",
  "Uploaded By",
  "Upload Date",
  "File Size",
  "Type",
  "Status",
  "Last Modified",
  "Archived"
];

const DocumentUploadTable = ({ searchQuery, searchTrigger, setTotalDocuments }) => {
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [documentsPerPage, setDocumentsPerPage] = useState(10);
  const [openDropdownRow, setOpenDropdownRow] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const dropdownRef = useRef(null);

  const token = Cookies.get("authToken");
  const officeCode = Cookies.get("officeCode");
  const dispatch = useDispatch();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpenDropdownRow(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    fetchDocuments();
  }, [searchTrigger, currentPage, documentsPerPage]);

  // Fetch documents
  const fetchDocuments = async () => {
    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/document-uploads/search?page=${currentPage - 1}&size=${documentsPerPage}`,
        {
          query: searchQuery.trim()
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            "Office-Code": officeCode,
          },
        }
      );

      const { content, totalElements, totalPages } = response.data;

      setDocuments(content);
      setTotalDocuments(totalElements);
      setTotalPages(totalPages);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching documents:", error);
      setTotalDocuments(0);
      setDocuments([]);
      setTotalPages(1);
    } finally {
      setIsLoading(false);
    }
  };

  const formattedDocuments = documents.map((doc) => ({
    "Document ID": doc?.documentId ?? "-",
    "Ascend Score": doc?.fileName ?? "-",
    "Uploaded By": doc?.uploadedBy ?? "-",
    "Upload Date": doc?.uploadDate ?? "-",
    "File Size": doc?.fileSize ?? "-",
    "Type": doc?.fileType ?? "-",
    "Status": doc?.status ?? "-",
    "Last Modified": doc?.lastModified ?? "-",
    "Archived": doc?.archived ? "Yes" : "No",
  }));

  const handleSelectAll = (checked) => {
    setSelectedDocuments(checked ? formattedDocuments.map(d => d["Document ID"]) : []);
  };

  const handleSingleCheckbox = (documentId) => {
    setSelectedDocuments(prev =>
      prev.includes(documentId)
        ? prev.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const handleViewDocument = (documentId) => {
    const newTabId = `view-doc-${documentId}-${Date.now()}`;
    dispatch(addTab({
      id: newTabId,
      name: `Document: ${documentId}`,
      contentType: "documentView",
      documentId,
      isDynamic: true,
      createdAt: Date.now(),
    }));
  };

  const handleEditDocument = (documentId) => {
      };

  const handleDeleteDocument = (documentId) => {
      };

  const handleActionClick = (documentId, e) => {
    e.stopPropagation();
    setOpenDropdownRow((prev) => (prev === documentId ? null : documentId));
  };

  const renderActionColumn = (row) => (
    <td
      onClick={(e) => e.stopPropagation()}
      className={`sticky left-[80px] bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border text-center ${
        openDropdownRow === row["Document ID"] ? "z-[20]" : "z-[10]"
      }`}
      style={{
        width: "50px",
        minWidth: "50px",
        maxWidth: "50px",
        boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
      }}
    >
      <div className="relative">
        <button
          className="p-1 text-gray-500 hover:text-gray-800 w-full relative z-[1]"
          onClick={(e) => handleActionClick(row["Document ID"], e)}
        >
          <MoreHorizontal size={18} className="mx-auto" />
        </button>

        {openDropdownRow === row["Document ID"] && (
          <ActionMenu
            rowId={row["Document ID"]}
            isOpen={openDropdownRow === row["Document ID"]}
            onClose={() => setOpenDropdownRow(null)}
            moduleName="Document"
            actions={[
              {
                icon: "Eye",
                label: "View",
                color: "blue",
                action: (id) => handleViewDocument(id),
              },
              {
                icon: "Edit",
                label: "Edit",
                color: "green",
                action: (id) => handleEditDocument(id),
              },
              {
                icon: "Trash",
                label: "Delete",
                color: "red",
                action: (id) => handleDeleteDocument(id),
              },
            ]}
          />
        )}
      </div>
    </td>
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  return (
    <div className="bg-white dark:bg-acs-dark-surface flex flex-col h-full">
      {isLoading ? (
        <Loader
          color="text-blue-600"
          size="h-5 w-5"
          message="Loading Documents...."
        />
      ) : (
        <>
          <div className="flex-1 min-h-[200px] bg-white dark:bg-acs-dark-surface sm:min-h-[300px] overflow-x-auto">
            <Table
              headers={TABLE_HEADERS}
              data={formattedDocuments}
              onSelectAll={handleSelectAll}
            >
              {(row) => (
                <>
                  <td
                    className="sticky left-0 bg-white dark:bg-acs-dark-surface border-b border-gray-200 dark:border-acs-dark-border"
                    onDoubleClick={() => handleViewDocument(row["Document ID"])}
                    style={{
                      width: "80px",
                      minWidth: "80px",
                      maxWidth: "80px",
                      boxShadow: "2px 0 2px -1px rgba(0,0,0,0.1)",
                    }}
                  >
                    <input
                      type="checkbox"
                      className="cursor-pointer mx-auto block"
                      checked={selectedDocuments.includes(row["Document ID"] || "")}
                      onChange={() => handleSingleCheckbox(row["Document ID"])}
                    />
                  </td>

                  {renderActionColumn(row)}

                  {TABLE_HEADERS.slice(2).map((header) => (
                    <td
                      key={header}
                      className="px-2 sm:px-3 py-1 sm:py-2 text-black text-xs sm:text-sm md:text-base border-b border-gray-200"
                      onDoubleClick={() => handleViewDocument(row["Document ID"])}
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "200px",
                      }}
                    >
                      {row[header]}
                    </td>
                  ))}
                </>
              )}
            </Table>
          </div>

          {/* Pagination */}
          <div className="flex justify-between border-t border-gray-200 dark:border-acs-dark-border items-center bg-white dark:bg-acs-dark-surface p-fluid-1">
            <div className="flex items-center">
              <span className="mr-2">Documents per page</span>
              <select
                value={documentsPerPage}
                onChange={(e) => {
                  setDocumentsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="border-b-2 border-black focus:outline-none"
              >
                {[5, 10, 25, 50].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center">
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="mx-2 text-gray-500 disabled:opacity-50"
              >
                ❮
              </button>
              <span>
                {currentPage} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="mx-2 text-gray-500 disabled:opacity-50"
              >
                ❯
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default DocumentUploadTable;
